// ================================================================================
// ECOMSIMPLY - PRODUCT RESEARCH FILTERS COMPONENT (CORRECTED)
// ================================================================================

import React from 'react';
import { Search, BarChart3, RefreshCw } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';

const ProductResearchFilters = ({ 
  filters = {}, 
  onFiltersChange,  // Cohérence avec ProductResearchPage
  onSearch, 
  loading = false 
}) => {
  
  const handleFilterChange = (key, value) => {
    if (typeof onFiltersChange === 'function') {
      onFiltersChange(prev => ({
        ...prev,
        [key]: value
      }));
    } else {
      console.warn('onFiltersChange is not a function:', typeof onFiltersChange);
    }
  };

  const resetFilters = () => {
    if (typeof onFiltersChange === 'function') {
      onFiltersChange(() => ({
        query: '',
        category: '',
        minPrice: '',
        maxPrice: '',
        minBSR: '',
        maxBSR: '',
        minRating: '',
        competition: '',
        marketplace: 'amazon.fr',
        budget: 10000,
        suppliers: 'verified',
        targetMargin: '',
        minROI: '',
        maxWeight: ''
      }));
    } else {
      console.warn('onFiltersChange is not a function:', typeof onFiltersChange);
    }
  };

  const competitionLevels = [
    { value: '', label: 'Toute concurrence' },
    { value: 'very_low', label: '🟢 Très faible' },
    { value: 'low', label: '🟡 Faible' },
    { value: 'medium', label: '🟠 Moyenne' },
    { value: 'high', label: '🔴 Élevée' }
  ];

  const supplierTypes = [
    { value: 'all', label: 'Tous fournisseurs' },
    { value: 'verified', label: '✅ Vérifiés seulement' },
    { value: 'gold', label: '🏆 Gold Supplier' },
    { value: 'trade_assurance', label: '🛡️ Trade Assurance' }
  ];

  const marketplaces = [
    { value: 'amazon.fr', label: '🇫🇷 Amazon France' },
    { value: 'amazon.de', label: '🇩🇪 Amazon Allemagne' },
    { value: 'amazon.es', label: '🇪🇸 Amazon Espagne' },
    { value: 'amazon.it', label: '🇮🇹 Amazon Italie' },
    { value: 'amazon.com', label: '🇺🇸 Amazon USA' },
    { value: 'amazon.co.uk', label: '🇬🇧 Amazon UK' }
  ];

  return (
    <Card className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Filtres de Recherche IA
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Configurez vos critères pour identifier des produits rentables
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-100 text-blue-700">
            🧠 IA GPT-5 PRO
          </Badge>
          <Badge className="bg-green-100 text-green-700">
            🌧️ Rainforest API
          </Badge>
        </div>
      </div>

      {/* Grid de filtres */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        
        {/* Recherche par mots-clés */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mots-clés produit
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={filters.query || ''}
              onChange={(e) => handleFilterChange('query', e.target.value)}
              placeholder="smart phone case, fitness tracker..."
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Marketplace */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Marketplace
          </label>
          <select
            value={filters.marketplace || 'amazon.fr'}
            onChange={(e) => handleFilterChange('marketplace', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {marketplaces.map(marketplace => (
              <option key={marketplace.value} value={marketplace.value}>
                {marketplace.label}
              </option>
            ))}
          </select>
        </div>

        {/* Concurrence */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Niveau de concurrence
          </label>
          <select
            value={filters.competition || ''}
            onChange={(e) => handleFilterChange('competition', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {competitionLevels.map(level => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>

        {/* Prix min */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Prix minimum (€)
          </label>
          <input
            type="number"
            value={filters.minPrice || ''}
            onChange={(e) => handleFilterChange('minPrice', e.target.value)}
            placeholder="15"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Prix max */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Prix maximum (€)
          </label>
          <input
            type="number"
            value={filters.maxPrice || ''}
            onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
            placeholder="200"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Marge cible */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Marge cible min (%)
          </label>
          <input
            type="number"
            value={filters.targetMargin || ''}
            onChange={(e) => handleFilterChange('targetMargin', e.target.value)}
            placeholder="30"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* ROI minimum */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ROI minimum (%)
          </label>
          <input
            type="number"
            value={filters.minROI || ''}
            onChange={(e) => handleFilterChange('minROI', e.target.value)}
            placeholder="50"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Budget d'investissement */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Budget d'investissement (€)
          </label>
          <input
            type="number"
            value={filters.budget || 10000}
            onChange={(e) => handleFilterChange('budget', Number(e.target.value) || 10000)}
            placeholder="10000"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Poids maximum */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Poids max (kg)
          </label>
          <input
            type="number"
            value={filters.maxWeight || ''}
            onChange={(e) => handleFilterChange('maxWeight', e.target.value)}
            placeholder="10"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* BSR minimum */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            BSR minimum
          </label>
          <input
            type="number"
            value={filters.minBSR || ''}
            onChange={(e) => handleFilterChange('minBSR', e.target.value)}
            placeholder="1000"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* BSR maximum */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            BSR maximum
          </label>
          <input
            type="number"
            value={filters.maxBSR || ''}
            onChange={(e) => handleFilterChange('maxBSR', e.target.value)}
            placeholder="50000"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Rating minimum */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rating minimum
          </label>
          <input
            type="number"
            value={filters.minRating || ''}
            onChange={(e) => handleFilterChange('minRating', e.target.value)}
            placeholder="4.0"
            step="0.1"
            min="1"
            max="5"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Type de fournisseurs */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type de fournisseurs
          </label>
          <select
            value={filters.suppliers || 'verified'}
            onChange={(e) => handleFilterChange('suppliers', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {supplierTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <Button
          onClick={resetFilters}
          variant="outline"
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Réinitialiser
        </Button>
        
        <Button
          onClick={onSearch}
          disabled={loading}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90"
        >
          <Search className="w-4 h-4" />
          {loading ? 'Recherche en cours...' : 'Lancer la recherche IA'}
        </Button>
      </div>
    </Card>
  );
};

export default ProductResearchFilters;